import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/primitives";
import { useSessionStore } from "@/stores/sessionStore";
import { useCreateFlavorDraftStore } from "@/stores/createFlavorDraftStore";
import { finalizeUploads, useAuthHeaders } from "@/services/uploads_api";
import {
  CreateFlavorError,
  createFlavor,
  finalizeFormulation,
  type CreateFlavorBody,
} from "@/services/flavor_api";
import { flavorKeys } from "@/hooks/useSidebarFlavors";
import { logEvent } from "@/lib/eventLogger";
import monitoring from "@/lib/monitoring";
import { SAMPLE_TARGET } from "@/services/mock";
import { withSessionConstraints } from "@/utils/formulation/withSessionConstraints";
import type { Session } from "@/utils/types/flavor";
import { PathCard } from "./steps/PathCard";
import {
  AdvancedSpecs,
  type AdvancedSpecsValue,
} from "./steps/AdvancedSpecs";
import { AnchorPanel } from "./steps/AnchorPanel";
import { DiscoverPanel } from "./steps/DiscoverPanel";
import { EMPTY_ANCHOR, type Anchor } from "./steps/anchorTypes";

type Path = "have-target" | "discover";

const ADVANCED_DEFAULTS: AdvancedSpecsValue = {
  basePresets: [],
  inventoryOnly: false,
  regScope: "",
  claim: null,
  application: null,
  form: null,
};

// Anthropic name generation runs async on the server after the create response
// returns. This delay gives it time to finish before we refetch the sidebar's
// recent list a second time to pick up the AI-generated name. If the call is
// slower than this, the next focus refetch (or a sidebar interaction) catches it.
const SIDEBAR_NAME_REFRESH_DELAY_MS = 5_000;

export function CreateFlavorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useSessionStore((s) => s.setSession);
  const queryClient = useQueryClient();

  const draft = useCreateFlavorDraftStore((s) => s.draft);
  const setDraft = useCreateFlavorDraftStore((s) => s.setDraft);
  const clearDraft = useCreateFlavorDraftStore((s) => s.clearDraft);

  const getHeaders = useAuthHeaders();
  const draftId = useCreateFlavorDraftStore((s) => s.draft?.draftId ?? "");

  // Ensure the draft exists at mount so draftId gets minted.
  useEffect(() => {
    if (!draft) setDraft({});
  }, [draft, setDraft]);

  // One-time flag — captured at mount only. We don't want this to flip to false
  // mid-edit and back to true on the next mount.
  const [didResume] = useState(() => draft !== null);
  const [resumePillVisible, setResumePillVisible] = useState(didResume);
  useEffect(() => {
    if (!didResume) return;
    const t = window.setTimeout(() => setResumePillVisible(false), 3000);
    return () => window.clearTimeout(t);
  }, [didResume]);

  // Hydrate from persisted draft on first render. Capture the draft snapshot at
  // mount-time so subsequent draft mutations from this same component don't
  // re-initialize state via React's useState lazy initializer.
  const [path, setPathState] = useState<Path>(draft?.path ?? "have-target");
  // Deep-default over EMPTY_ANCHOR so a draft persisted before a newer anchor
  // field existed (e.g. chromaConfigById, pickedLibRef) hydrates with that field
  // present rather than `undefined` — otherwise an old library/chroma draft
  // crashes the page on resume (the seeding effect's `in` op + bracket reads).
  const [anchor, setAnchorState] = useState<Anchor>({
    ...EMPTY_ANCHOR,
    ...draft?.anchor,
  });
  const [advanced, setAdvancedState] = useState<AdvancedSpecsValue>(
    draft?.advanced ?? ADVANCED_DEFAULTS,
  );
  const [isReferenceNameDuplicate, setIsReferenceNameDuplicate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Neutral inline notice for the not-yet-wired anchor paths (visual-only).
  const [submitNotice, setSubmitNotice] = useState<string | null>(null);

  // Lead base preset change → pulse the recommendation re-rank chip.
  const [reRankPulse, setReRankPulse] = useState(false);
  useEffect(() => {
    if (!reRankPulse) return;
    const t = setTimeout(() => setReRankPulse(false), 1800);
    return () => clearTimeout(t);
  }, [reRankPulse]);

  // A full_cycle generation that failed (worker mid-pipeline) redirects back here
  // with this flag; the draft is preserved (files/formulations/specs intact). Show
  // the generic error by Start Designing, then clear the history state so a
  // refresh / re-render doesn't re-surface it.
  useEffect(() => {
    const st = location.state as { generationFailed?: boolean } | null;
    if (st?.generationFailed) {
      setSubmitError("Something went wrong");
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Setters that also persist to the draft store.
  const setPath = (next: Path) => {
    setPathState(next);
    setDraft({ path: next });
  };
  const setAnchor = (next: Anchor) => {
    setAnchorState(next);
    setDraft({ anchor: next });
    setSubmitNotice(null);
  };
  const setAdvanced = (next: AdvancedSpecsValue) => {
    setAdvancedState(next);
    setDraft({ advanced: next });
  };

  // ── Validation — the user must anchor the reference somehow. ──
  const anchorReady = (() => {
    if (anchor.mode === "catalog") return !!anchor.pickedRec;
    if (anchor.mode === "library") {
      if (anchor.libMode === "select") return !!anchor.pickedLibId;
      if (anchor.libMode === "chroma")
        return (
          anchor.newName.trim().length > 0 &&
          anchor.chromas.length > 0 &&
          anchor.chromas.every((c) => c.status === "uploaded") &&
          !isReferenceNameDuplicate
        );
      if (anchor.libMode === "import")
        return (
          anchor.newName.trim().length > 0 &&
          !!anchor.formulaImport &&
          anchor.formulaImport.rows.length > 0 &&
          anchor.formulaImport.rows.every(
            (r) => r.resolved && typeof r.ppm === "number" && r.doseStatus !== "unconvertible",
          )
        );
    }
    return false; // describe — locked in Pilot
  })();
  const canSubmit =
    (path === "have-target" && anchorReady) || path === "discover";

  const readinessMsg = canSubmit
    ? null
    : path !== "have-target"
    ? "Choose how you'd like to start"
    : anchor.mode === "catalog"
    ? "Pick a reference flavor to continue"
    : anchor.mode === "describe"
    ? "Describe-the-target unlocks after Pilot — pick another tab to continue"
    : anchor.libMode === "select"
    ? "Select a substance from your library to continue"
    : anchor.libMode === "chroma"
    ? anchor.chromas.length === 0
      ? "Upload at least one chromatogram to continue"
      : !anchor.chromas.every((c) => c.status === "uploaded")
      ? "Wait for the upload to finish"
      : anchor.newName.trim().length === 0
      ? "Name this substance to continue"
      : isReferenceNameDuplicate
      ? "Pick a different name to continue"
      : null
    : !anchor.formulaImport
    ? "Import a formulation to continue"
    : !anchor.newName.trim()
    ? "Name this flavor to continue"
    : anchor.formulaImport.rows.length === 0
    ? "Add at least one ingredient to continue"
    : anchor.formulaImport.rows.some((r) => !r.resolved)
    ? "Resolve or remove every unmatched ingredient to continue"
    : anchor.formulaImport.rows.some(
        (r) => typeof r.ppm !== "number" || r.doseStatus === "unconvertible",
      )
    ? "Enter a ppm dose for every flagged ingredient to continue"
    : null;

  const submit = async () => {
    if (!canSubmit || submitting) return;

    // Analytics: record the click intent + the parameters the user selected.
    // Fires BEFORE the network round-trip so we capture abandoned/failed
    // creates too.
    const isChroma = anchor.mode === "library" && anchor.libMode === "chroma";
    logEvent("flavor_create_submitted", {
      path,
      description: null,
      anchor_mode: path === "have-target" ? anchor.mode : null,
      catalog_board_id:
        path === "have-target" && anchor.mode === "catalog" && anchor.pickedRec
          ? anchor.pickedRec.id
          : null,
      catalog_board_name:
        path === "have-target" && anchor.mode === "catalog" && anchor.pickedRec
          ? anchor.pickedRec.name
          : null,
      chromatogram_reference_name: isChroma ? anchor.newName.trim() || null : null,
      chromatogram_file_count: isChroma ? anchor.chromas.length : 0,
      base_preset_count: advanced.basePresets.length,
      base_preset_kinds: advanced.basePresets.map((b) => b.kind).join(",") || null,
      inventory_only: advanced.inventoryOnly,
      regulatory_scope: advanced.regScope || null,
      claim: advanced.claim,
      application: advanced.application,
      form: advanced.form,
    });

    setSubmitting(true);
    setSubmitError(null);
    setSubmitNotice(null);

    const advancedPayload: CreateFlavorBody["advanced"] = {
      basePresets: advanced.basePresets.map((b) =>
        b.kind === "flavor"
          ? { kind: "flavor" as const, id: b.id }
          : b.kind === "food"
            ? { kind: "food" as const, id: b.id }
            : { kind: "inventory" as const, id: b.id },
      ),
      inventoryOnly: advanced.inventoryOnly,
      regScope: advanced.regScope ? advanced.regScope : null,
      claim: advanced.claim,
      application: advanced.application,
      form: advanced.form,
    };
    const baseSession: Omit<Session, "id" | "label" | "mode" | "needsGeneration"> = {
      baseFormula: advanced.basePresets[0]
        ? {
            kind: advanced.basePresets[0].kind,
            id: advanced.basePresets[0].id,
            name: advanced.basePresets[0].title,
          }
        : null,
      inventoryOnly: advanced.inventoryOnly,
      regScope: advanced.regScope,
      claim: advanced.claim ?? undefined,
      application: advanced.application ?? undefined,
      form: advanced.form ?? undefined,
    };

    const refreshSidebar = () => {
      void queryClient.invalidateQueries({ queryKey: flavorKeys.recent });
      window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: flavorKeys.recent });
      }, SIDEBAR_NAME_REFRESH_DELAY_MS);
    };

    try {
      // ── DISCOVER: no anchor, no TARGET, just create the flavor with advanced specs.
      if (path === "discover") {
        const { flavorId } = await createFlavor({
          path: "discover",
          description: "",
          anchor: null,
          advanced: advancedPayload,
        });
        const session: Session = {
          ...baseSession,
          id: String(flavorId),
          label: "New flavor",
          mode: "scratch",
          needsGeneration: false,
          target: null,
          formulation: [],
        };
        setSession(session);
        refreshSidebar();
        navigate(`/flavors/${flavorId}`);
        clearDraft();
        return;
      }

      // ── HAVE-TARGET: catalog pick — TARGET resolved server-side from the board row.
      if (anchor.mode === "catalog" && anchor.pickedRec) {
        const catalogId = Number(anchor.pickedRec.id);
        if (!Number.isFinite(catalogId)) {
          setSubmitError("Something went wrong");
          return;
        }
        const { flavorId } = await createFlavor({
          path: "have-target",
          description: "",
          anchor: { mode: "catalog", catalogId },
          advanced: advancedPayload,
        });
        const draftSession: Session = {
          ...baseSession,
          id: String(flavorId),
          label: anchor.pickedRec.name,
          mode: "recommend",
          needsGeneration: false,
          formulation: [],
        };
        const session: Session = {
          ...draftSession,
          target: withSessionConstraints(SAMPLE_TARGET, draftSession),
        };
        setSession(session);
        refreshSidebar();
        navigate(`/flavors/${flavorId}`);
        clearDraft();
        return;
      }

      // ── HAVE-TARGET: own library → add new substance from chromatograms.
      // Finalize the upload (creates the food row + platform.chromatogram rows,
      // with per-file experiment configs), then create the flavor with TARGET =
      // that food (which enqueues the full_cycle pipeline), then go through the
      // generating page while the chromatogram analysis runs.
      if (
        anchor.mode === "library" &&
        anchor.libMode === "chroma" &&
        anchor.chromas.length > 0
      ) {
        if (!draftId) return; // shouldn't happen post-mount
        if (!anchor.chromas.every((c) => c.status === "uploaded")) return;
        if (!anchor.newName.trim()) return;

        // Per-file experiment config: only files with a real (non-temp) id and
        // an assigned config. Omit the array entirely when nothing's assigned.
        const configs = anchor.chromas
          .filter(
            (c) =>
              c.status === "uploaded" &&
              !c.fileUploadId.startsWith("tmp-") &&
              anchor.chromaConfigById[c.fileUploadId],
          )
          .map((c) => ({
            file_upload_id: c.fileUploadId,
            user_exp_config_id: anchor.chromaConfigById[c.fileUploadId],
          }));

        const headers = await getHeaders();
        const finalized = await finalizeUploads(
          {
            draft_id: draftId,
            name: anchor.newName.trim(),
            kind: "chromatogram",
            configs: configs.length > 0 ? configs : undefined,
          },
          headers,
        );
        const { flavorId } = await createFlavor({
          path: "have-target",
          description: "",
          anchor: { mode: "chromatogram", foodId: finalized.food_id },
          advanced: advancedPayload,
        });
        const session: Session = {
          ...baseSession,
          id: String(flavorId),
          label: anchor.newName.trim(),
          mode: "recommend",
          needsGeneration: true,
        };
        setSession(session);
        refreshSidebar();
        // Draft is cleared by GeneratingPage on SSE `done` (success) — NOT here —
        // so a generation failure can redirect back to New Flavor with the draft
        // (files / formulations / specs) intact.
        navigate("/flavors/new/generating");
        return;
      }

      // ── HAVE-TARGET: own library → select an existing substance as TARGET.
      // The picked library item resolves to a public.food | public.compound
      // row; the create endpoint records it as a `library`-source flavor_target.
      // No pipeline run is needed (the substance already exists) — the workbench
      // hydrates the real target profile server-side.
      if (anchor.mode === "library" && anchor.libMode === "select") {
        if (!anchor.pickedLibRef) return;
        const { flavorId } = await createFlavor({
          path: "have-target",
          description: "",
          anchor: {
            mode: "library",
            ndbTable: anchor.pickedLibRef.ndbTable,
            rowId: anchor.pickedLibRef.rowId,
          },
          advanced: advancedPayload,
        });
        const draftSession: Session = {
          ...baseSession,
          id: String(flavorId),
          label: anchor.pickedLibRef.name || "New flavor",
          mode: "recommend",
          needsGeneration: false,
          formulation: [],
        };
        const session: Session = {
          ...draftSession,
          target: withSessionConstraints(SAMPLE_TARGET, draftSession),
        };
        setSession(session);
        refreshSidebar();
        navigate(`/flavors/${flavorId}`);
        clearDraft();
        return;
      }
      // ── HAVE-TARGET: own library → import formulation. Persist the confirmed
      // dose table as user_uploaded_formula + a new public.food, then create the
      // flavor with TARGET = that food (which enqueues the full_cycle pipeline),
      // then go through the generating page while N-Food derives the profile.
      if (anchor.mode === "library" && anchor.libMode === "import") {
        const fi = anchor.formulaImport;
        if (!fi || fi.rows.length === 0 || !anchor.newName.trim()) return;
        if (
          fi.rows.some(
            (r) => !r.resolved || typeof r.ppm !== "number" || r.doseStatus === "unconvertible",
          )
        )
          return; // gated by readiness too

        const finalized = await finalizeFormulation({
          name: anchor.newName.trim(),
          rows: fi.rows.map((r) => ({
            raw: r.raw || null,
            // r.resolved is non-null here (guarded above).
            resolved_ndb_table: r.resolved!.ndbTable,
            resolved_row_id: r.resolved!.rowId,
            ppm: Number(r.ppm) || 0,
            unit: "ppm",
          })),
          source_file_upload_id: fi.sourceFileUploadId ?? null,
        });
        const { flavorId } = await createFlavor({
          path: "have-target",
          description: "",
          anchor: {
            mode: "formulation",
            foodId: finalized.food_id,
            userUploadedFormulaId: finalized.user_uploaded_formula_id,
          },
          advanced: advancedPayload,
        });
        const session: Session = {
          ...baseSession,
          id: String(flavorId),
          label: anchor.newName.trim(),
          mode: "recommend",
          needsGeneration: true,
        };
        setSession(session);
        refreshSidebar();
        // Draft is cleared by GeneratingPage on SSE `done` (success) — NOT here —
        // so a generation failure can redirect back to New Flavor with the draft
        // (files / formulations / specs) intact.
        navigate("/flavors/new/generating");
        return;
      }
    } catch (err) {
      if (err instanceof CreateFlavorError) {
        setSubmitError(err.serverMessage ?? "Something went wrong");
      } else {
        monitoring.error(
          "create_flavor_failed",
          err instanceof Error ? err : new Error(String(err)),
        );
        setSubmitError("Something went wrong");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const advancedSpecs = (
    <AdvancedSpecs
      value={advanced}
      onChange={setAdvanced}
      onLeadBaseChange={() => setReRankPulse(true)}
    />
  );

  return (
    <div style={{ overflow: "auto", height: "100%", flex: 1 }}>
      <div className="create-page fade-in">
        <div className="label" style={{ marginBottom: 10 }}>
          New flavor
        </div>
        <div className="display create-headline">
          What flavor do you want to create?
        </div>

        {resumePillVisible && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              background: "var(--bg-sunk)",
              color: "var(--ink-3)",
              fontSize: 11,
              marginTop: 4,
            }}
            role="status"
          >
            <Icon name="check" size={11} />
            Resumed your draft
          </div>
        )}

        <div className="path-grid">
          <PathCard
            active={path === "have-target"}
            primary
            icon="target"
            title="Do you have a specific target flavor in mind?"
            desc="Share a reference flavor, a substance from your library, or a chromatogram — we'll anchor the workbench to your target."
            onClick={() => setPath("have-target")}
          />
          <PathCard
            active={path === "discover"}
            icon="compass"
            title="Or find one as you formulate?"
            desc="Start from a blank flask and explore other flavors as you go."
            onClick={() => setPath("discover")}
          />
        </div>

        {path === "have-target" && (
          <div className="slide-up">
            {advancedSpecs}

            <AnchorPanel
              draftId={draftId}
              value={anchor}
              onChange={setAnchor}
              baseFamily={null}
              reRankPulse={reRankPulse}
              onDuplicateNameChange={setIsReferenceNameDuplicate}
              onOpenLibrary={() => navigate("/ingredients")}
            />
          </div>
        )}

        {path === "discover" && (
          <>
            <DiscoverPanel />
            {advancedSpecs}
          </>
        )}

        <div className="create-submit-row">
          {submitError ? (
            <span style={{ fontSize: 12, color: "var(--danger, #b91c1c)" }}>
              {submitError}
            </span>
          ) : submitNotice ? (
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
              {submitNotice}
            </span>
          ) : readinessMsg ? (
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
              {readinessMsg}
            </span>
          ) : null}
          <Button
            type="button"
            variant="primary"
            disabled={!canSubmit || submitting}
            onClick={submit}
            style={{ opacity: !canSubmit || submitting ? 0.4 : 1 }}
          >
            {submitting
              ? "Creating…"
              : path === "discover"
              ? "Open a blank flask"
              : "Start designing"}
            <Icon name="arrow-r" size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
